import { useState, useEffect, useRef, useCallback } from 'react';

export interface UseIntersectionObserverOptions {
  threshold?: number | number[];
  root?: Element | null;
  rootMargin?: string;
  freezeOnceVisible?: boolean;
}

/**
 * Hook for intersection observer functionality
 * Useful for lazy loading, infinite scroll, etc.
 */
export const useIntersectionObserver = (
  options: UseIntersectionObserverOptions = {}
): [React.RefObject<HTMLElement>, boolean] => {
  const { threshold = 0.1, root = null, rootMargin = '0px', freezeOnceVisible = false } = options;

  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const disconnect = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  const observe = useCallback(() => {
    if (elementRef.current && !observerRef.current) {
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          const isElementVisible = entry.isIntersecting;
          setIsVisible(isElementVisible);

          if (isElementVisible && freezeOnceVisible) {
            disconnect();
          }
        },
        {
          threshold,
          root,
          rootMargin,
        }
      );

      observerRef.current.observe(elementRef.current);
    }
  }, [threshold, root, rootMargin, freezeOnceVisible, disconnect]);

  useEffect(() => {
    observe();
    return disconnect;
  }, [observe, disconnect]);

  return [elementRef, isVisible];
};
